import { JSX, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { MoreVertical, ChevronRight } from 'lucide-react';

interface IActions {
	label?: string;
	onClick?: () => void;
	style?: string;
	component?: JSX.Element;
	inVisible?: boolean;
}

export default function TableActionsComponent({
	actions,
}: {
	actions: Array<IActions>;
}) {
	const [showActions, setShowActions] = useState(false);
	const [coord, setCoord] = useState({ x: 0, y: 0 });
	const triggerRef = useRef<HTMLDivElement>(null);

	const handleToggle = (event: React.MouseEvent) => {
		event.stopPropagation();
		const rect = triggerRef.current?.getBoundingClientRect();
		if (rect) {
			setCoord({
				x: rect.left,
				y: rect.top + rect.height,
			});
		}
		setShowActions(!showActions);
	};

	return (
		<div
			ref={triggerRef}
			className='flex justify-center items-center w-full h-full'>
			<button
				onClick={handleToggle}
				className={`p-1.5 rounded-md hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-all ${showActions ? 'bg-neutral-100 text-neutral-900 shadow-sm' : ''}`}>
				<MoreVertical size={16} />
			</button>

			{showActions && (
				<CustomCTXMenu
					actions={actions}
					setShowActions={setShowActions}
					x={coord.x}
					y={coord.y}
				/>
			)}
		</div>
	);
}

function useClickOutside(ref: React.RefObject<any>, func: () => void) {
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				func();
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ref]);
}

const CustomCTXMenu = ({
	setShowActions,
	actions,
	x,
	y,
}: {
	setShowActions: (show: boolean) => void;
	actions: Array<IActions>;
	x: number;
	y: number;
}) => {
	const menuBody = useRef<HTMLDivElement>(null);
	useClickOutside(menuBody, () => setShowActions(false));

	return ReactDOM.createPortal(
		<div
			ref={menuBody}
			style={{
				top: Math.min(y + 6, window.innerHeight - 250),
				left: Math.max(16, x - 160),
			}}
			className='fixed flex flex-col min-w-[170px] p-1 rounded-md bg-white shadow-xl border border-neutral-100 z-[9999] animate-in fade-in zoom-in-95 duration-150'>
			{actions.map((action, index) => {
				if (action.inVisible) return null;

				if (action.component) {
					return (
						<div key={index} className='px-3 py-1.5'>
							{action.component}
						</div>
					);
				}

				return (
					<button
						key={index}
						type='button'
						className={`w-full flex items-center justify-between px-3 py-1.5 text-xs font-bold rounded-md transition-colors hover:bg-neutral-50 text-neutral-600 hover:text-neutral-900 group ${action.style}`}
						onClick={(e) => {
							e.stopPropagation();
							action.onClick?.();
							setShowActions(false);
						}}>
						<span>{action.label}</span>
						<ChevronRight
							size={12}
							className='opacity-0 group-hover:opacity-40 transition-opacity'
						/>
					</button>
				);
			})}
		</div>,
		document.body,
	);
};
